import React, { useCallback, useEffect, useState } from "react";
import { Button, Input } from "antd";
import moment from "moment";
import { protectedApi } from "../../../app/utils/common/protectedApi";
import { APIS } from "../../../app/utils/constants/apiConstant";
import { showErrorResponse } from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import { TEAM_PRAISE_UPDATED_EVENT } from "../../../app/utils/constants/teamPraiseEvents";

const TeamPraisePage = () => {
    const [message, setMessage] = useState("");
    const [praiseLabel, setPraiseLabel] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [meta, setMeta] = useState({ authorName: null, updatedAt: null });

    const load = useCallback(async (opts = {}) => {
        const silent = Boolean(opts.silent);
        if (!silent) setLoading(true);
        try {
            const response = await protectedApi({
                url: APIS.PRAISE_CURRENT,
                method: "GET",
            });
            const payload = response?.data?.data ?? {};
            // Keep compose box empty after load; published message appears on dashboard.
            setMessage("");
            setPraiseLabel(
                typeof payload.praiseLabel === "string" && payload.praiseLabel.trim()
                    ? payload.praiseLabel.trim()
                    : ""
            );
            setMeta({
                authorName: payload.authorName ?? null,
                updatedAt: payload.updatedAt ?? null,
            });
        } catch (error) {
            showErrorResponse(error);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleSave = async () => {
        const trimmed = typeof message === "string" ? message.trim() : "";
        if (trimmed.length > 2000) {
            showErrorToast("Message must be at most 2000 characters.");
            return;
        }
        const labelTrimmed =
            typeof praiseLabel === "string" ? praiseLabel.trim().slice(0, 120) : "";
        setSaving(true);
        try {
            const response = await protectedApi({
                url: APIS.PRAISE_CURRENT,
                method: "PUT",
                data: { message: trimmed, praiseLabel: labelTrimmed },
            });
            setMessage("");
            window.dispatchEvent(new Event(TEAM_PRAISE_UPDATED_EVENT));
            await load({ silent: true });
            showSuccessToast(response?.data?.message || "Team praise updated.");
        } catch (error) {
            showErrorResponse(error);
        } finally {
            setSaving(false);
        }
    };

    const when =
        meta.updatedAt && moment(meta.updatedAt).isValid()
            ? moment(meta.updatedAt).format("D MMM YYYY, h:mm A")
            : null;

    return (
        <div>
            <div className="mb-4">
                <h1 className="page-title">Team praise</h1>
                <p className="page-subtitle">
                    Post a message for everyone to see on the dashboard home screen. Only roles with the
                    Praise messages permission can update it.
                </p>
            </div>
            <div className="max-w-3xl border border-solid border-[#e7ebf6] rounded-[14px] p-6 bg-white shadow-sm">
                {loading ? (
                    <p className="text-gray-500">Loading…</p>
                ) : (
                    <>
                        <label className="block text-sm font-semibold text-[#202b44] mb-2" htmlFor="team-praise-label">
                            Praise type (shown on dashboard)
                        </label>
                        <Input
                            id="team-praise-label"
                            value={praiseLabel}
                            onChange={(e) => setPraiseLabel(e.target.value)}
                            placeholder="e.g. Team praise, Spotlight, Thank you"
                            maxLength={120}
                            showCount
                            className="mb-4"
                        />
                        <label className="block text-sm font-semibold text-[#202b44] mb-2" htmlFor="team-praise-message">
                            Message
                        </label>
                        <Input.TextArea
                            id="team-praise-message"
                            rows={6}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Thank the team, celebrate a win, or share encouragement…"
                            maxLength={2000}
                            showCount
                            className="mb-4"
                        />
                        <div className="flex flex-wrap items-center gap-3">
                            <Button type="primary" onClick={handleSave} loading={saving}>
                                Publish to dashboard
                            </Button>
                            {when ? (
                                <span className="text-sm text-gray-500">
                                    Last published{meta.authorName ? ` by ${meta.authorName}` : ""} — {when}
                                </span>
                            ) : null}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TeamPraisePage;
